import { Add, Close } from '@mui/icons-material';
import { Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuoteContext } from '../QuoteContextProvider';
import { commonInputProps } from '../QuoteDetailsForm/QuoteDetailsForm.common';
import { QuoteFormData } from '../QuoteDetailsForm/QuoteDetailsForm.schema';
import { TsrctQuoteMode } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useFormFieldErrorTranslation } from '~/hooks';

const editModeMap: { [key in TsrctQuoteMode]: boolean } = {
  [TsrctQuoteMode.NOOP]: false,
  [TsrctQuoteMode.UPLOAD]: true,
  [TsrctQuoteMode.EDIT]: true,
  [TsrctQuoteMode.BID_SHEET]: false,
  [TsrctQuoteMode.BID_SHEET_PROPOSAL]: false,
};

export const TermsForm = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationQuoteDetailsWidget' });
  const { errorToHelperText } = useFormFieldErrorTranslation();

  const { fields, remove, append } = useFieldArray<QuoteFormData, 'quote.paymentTerms'>({
    name: 'quote.paymentTerms', // unique name for your Field Array
    shouldUnregister: true,
  });

  const { quoteMode } = useQuoteContext();
  const edit = editModeMap[quoteMode];

  return (
    <Grid container spacing={5}>
      <Grid
        size={{
          xs: 12,
          md: 'auto',
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {t('paymentTerms')}
        </Typography>
      </Grid>
      <Grid
        container
        spacing={3}
        size="grow"
        sx={{
          alignContent: 'flex-start',
        }}
      >
        {fields.map((term, termIndex) => {
          return (
            <Grid key={term.id} container direction="row" spacing={2} size={12}>
              <Grid>
                <Controller
                  name={`quote.paymentTerms.${termIndex}.daysToPayment`}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('daysToPayment')}
                      error={!!error}
                      helperText={errorToHelperText(error)}
                      type="number"
                      {...commonInputProps}
                      fullWidth={false}
                      sx={{ width: 110 }}
                      {...field}
                      disabled={!edit}
                      onChange={(e) => {
                        // need to parse string to number specifically for zod form resolver
                        field.onChange(parseInt(e.target.value));
                      }}
                      slotProps={{
                        input: {
                          startAdornment: <InputAdornment position="start">Days</InputAdornment>,
                        },

                        inputLabel: { shrink: true },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid>
                <Controller
                  name={`quote.paymentTerms.${termIndex}.discount`}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <TextField
                        {...field}
                        disabled={!edit}
                        value={!field.value || isNaN(field.value) ? 0 : parseFloat((field.value * 100).toPrecision(12))}
                        onChange={(e) => {
                          const output = parseFloat(e.target.value);
                          field.onChange(isNaN(output) ? 0 : output / 100);
                        }}
                        // defaultValue={field.value}
                        label={t('discount')}
                        error={!!error}
                        // helperText={errorToHelperText(errors.name)}
                        type="number"
                        {...commonInputProps}
                        fullWidth={false}
                        sx={{ width: 110 }}
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">%</InputAdornment>,
                          },

                          inputLabel: { shrink: true },
                        }}
                      />
                    );
                  }}
                />
              </Grid>
              {edit ? (
                <Grid>
                  <Button
                    sx={{ height: '40px', width: '40px' }}
                    variant="outlined"
                    onClick={() => {
                      remove(termIndex);
                    }}
                  >
                    <Close />
                  </Button>
                </Grid>
              ) : null}
            </Grid>
          );
        })}
        {edit ? (
          <Grid size={12}>
            <Button
              variant="outlined"
              onClick={() => {
                append({
                  daysToPayment: 0,
                  discount: 0,
                });
              }}
              startIcon={<Add />}
              data-intercom-target="Quote/Terms/Add"
            >
              {t('addPaymentTermButton')}
            </Button>
          </Grid>
        ) : null}
      </Grid>
    </Grid>
  );
};
